import React from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import CheckIcon from '@mui/icons-material/Check'

export interface StepperStepMeta {
  id: string
  label: string
  sub?: string
}

export interface StepperProps {
  steps: StepperStepMeta[]
  activeId: string
  /** Furthest step index the user may jump to. Defaults to the active index (no skipping ahead). */
  reachedIndex?: number
  onStepClick?: (id: string) => void
  orientation?: 'vertical' | 'horizontal'
  /** Horizontal only — numbered dots + connecting line, no labels. For compact mobile chrome. */
  compact?: boolean
  className?: string
}

type DotState = 'done' | 'active' | 'upcoming'

const VList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
})

const HList = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  width: '100%',
})

const VRow = styled('button')({
  all: 'unset',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--ds-space-3)',
  width: '100%',
  cursor: 'pointer',
  padding: 'var(--ds-space-2) 0',
  '&:disabled': { cursor: 'default' },
  '&:focus-visible .ds-stepper-dot': {
    outline: '2px solid var(--ds-ink)',
    outlineOffset: 2,
  },
})

const HRow = styled('button', {
  shouldForwardProp: (p) => p !== 'compact',
})<{ compact?: boolean }>(({ compact }) => ({
  all: 'unset',
  display: 'flex',
  alignItems: 'center',
  flexDirection: compact ? 'row' : 'column',
  gap: compact ? 0 : 'var(--ds-space-2)',
  flex: 1,
  cursor: 'pointer',
  textAlign: compact ? 'left' : 'center',
  '&:disabled': { cursor: 'default' },
  '&:focus-visible .ds-stepper-dot': {
    outline: '2px solid var(--ds-ink)',
    outlineOffset: 2,
  },
}))

const Dot = styled(Box, {
  shouldForwardProp: (p) => p !== 'state' && p !== 'dotSize',
})<{ state: DotState; dotSize: number }>(({ state, dotSize }) => ({
  width: dotSize,
  height: dotSize,
  minWidth: dotSize,
  borderRadius: 'var(--ds-radius-pill)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: dotSize >= 24 ? 12 : 11,
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
  transition: [
    'background var(--ds-duration-normal) var(--ds-ease)',
    'color var(--ds-duration-normal) var(--ds-ease)',
    'box-shadow var(--ds-duration-normal) var(--ds-ease)',
  ].join(', '),
  ...(state === 'active' && {
    backgroundColor: 'var(--ds-accent)',
    color: 'var(--ds-accent-ink)',
    boxShadow: 'var(--ds-shadow-soft)',
  }),
  ...(state === 'done' && {
    backgroundColor: 'var(--ds-ink)',
    color: 'var(--ds-inverse-ink)',
  }),
  ...(state === 'upcoming' && {
    backgroundColor: 'var(--ds-hover)',
    color: 'var(--ds-ink-subtle)',
    border: '1px solid var(--ds-border)',
  }),
}))

const VLine = styled(Box, {
  shouldForwardProp: (p) => p !== 'done',
})<{ done?: boolean }>(({ done }) => ({
  width: 2,
  minHeight: 18,
  marginLeft: 13,
  backgroundColor: done ? 'var(--ds-ink)' : 'var(--ds-border)',
  transition: 'background var(--ds-duration-normal) var(--ds-ease)',
}))

const HLine = styled(Box, {
  shouldForwardProp: (p) => p !== 'done',
})<{ done?: boolean }>(({ done }) => ({
  height: 2,
  flex: 1,
  marginTop: 13,
  backgroundColor: done ? 'var(--ds-ink)' : 'var(--ds-border)',
  transition: 'background var(--ds-duration-normal) var(--ds-ease)',
}))

function dotState(index: number, activeIndex: number): DotState {
  if (index < activeIndex) return 'done'
  if (index === activeIndex) return 'active'
  return 'upcoming'
}

/** Numbered step navigation — click-to-jump rail (vertical) or compact top strip (horizontal). */
export function Stepper({
  steps,
  activeId,
  reachedIndex,
  onStepClick,
  orientation = 'vertical',
  compact,
  className,
}: StepperProps) {
  const activeIndex = Math.max(0, steps.findIndex((s) => s.id === activeId))
  const maxReachable = reachedIndex ?? activeIndex

  if (orientation === 'horizontal') {
    return (
      <HList className={className} role='tablist' aria-label='Wizard steps'>
        {steps.map((s, i) => {
          const state = dotState(i, activeIndex)
          const disabled = i > maxReachable || !onStepClick
          return (
            <React.Fragment key={s.id}>
              <HRow
                compact={compact}
                role='tab'
                type='button'
                aria-selected={state === 'active'}
                disabled={disabled}
                onClick={() => !disabled && onStepClick?.(s.id)}
              >
                <Dot className='ds-stepper-dot' state={state} dotSize={compact ? 20 : 26}>
                  {state === 'done' ? <CheckIcon sx={{ fontSize: 12 }} /> : i + 1}
                </Dot>
                {!compact && (
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: state === 'active' ? 600 : 500,
                      color: state === 'upcoming' ? 'var(--ds-ink-subtle)' : 'var(--ds-ink)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {s.label}
                  </Typography>
                )}
              </HRow>
              {i < steps.length - 1 && <HLine done={i < activeIndex} />}
            </React.Fragment>
          )
        })}
      </HList>
    )
  }

  return (
    <VList className={className} role='tablist' aria-label='Wizard steps'>
      {steps.map((s, i) => {
        const state = dotState(i, activeIndex)
        const disabled = i > maxReachable || !onStepClick
        return (
          <Box key={s.id}>
            <VRow
              role='tab'
              type='button'
              aria-selected={state === 'active'}
              disabled={disabled}
              onClick={() => !disabled && onStepClick?.(s.id)}
            >
              <Dot className='ds-stepper-dot' state={state} dotSize={26}>
                {state === 'done' ? <CheckIcon sx={{ fontSize: 13 }} /> : i + 1}
              </Dot>
              <Box minWidth={0} pt='2px'>
                <Typography
                  sx={{
                    fontSize: 13.5,
                    fontWeight: state === 'active' ? 600 : 500,
                    letterSpacing: '-0.01em',
                    color: state === 'upcoming' ? 'var(--ds-ink-subtle)' : 'var(--ds-ink)',
                  }}
                >
                  {s.label}
                </Typography>
                {s.sub && (
                  <Typography sx={{ fontSize: 11.5, color: 'var(--ds-ink-subtle)', mt: '1px' }}>
                    {s.sub}
                  </Typography>
                )}
              </Box>
            </VRow>
            {i < steps.length - 1 && <VLine done={i < activeIndex} />}
          </Box>
        )
      })}
    </VList>
  )
}
